/**
 * Report Parameter Formatting Utilities
 * 
 * Provides comprehensive utilities for validating, formatting, and filtering
 * parameters for report API calls based on user roles and data requirements.
 */

// Date validation and formatting utilities
export const dateUtils = {
    // Validate YYYY-MM-DD format
    isValidDate: (dateString) => {
        if (typeof dateString !== 'string') return false;
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(dateString)) return false;

        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date) && dateString === date.toISOString().split('T')[0];
    },

    // Format date to YYYY-MM-DD
    formatDate: (date) => {
        if (!date) return null;
        if (typeof date === 'string' && dateUtils.isValidDate(date)) return date;

        const dateObj = new Date(date);
        if (isNaN(dateObj)) return null;

        return dateObj.toISOString().split('T')[0];
    },

    // Validate date range
    validateDateRange: (startDate, endDate) => {
        if (!startDate || !endDate) return { valid: false, error: 'Both startDate and endDate are required' };

        if (!dateUtils.isValidDate(startDate)) {
            return { valid: false, error: 'startDate must be in YYYY-MM-DD format' };
        }

        if (!dateUtils.isValidDate(endDate)) {
            return { valid: false, error: 'endDate must be in YYYY-MM-DD format' };
        }

        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start > end) {
            return { valid: false, error: 'startDate must be before or equal to endDate' };
        }

        // Check if date range is reasonable (not more than 2 years)
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 730) {
            return { valid: false, error: 'Date range cannot exceed 2 years' };
        }

        return { valid: true };
    },

    // Get default date range (last 30 days)
    getDefaultDateRange: () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        return {
            startDate: dateUtils.formatDate(startDate),
            endDate: dateUtils.formatDate(endDate)
        };
    }
};

// ID validation and normalization utilities
export const idUtils = {
    // Validate and normalize clinic ID
    normalizeClinicId: (clinicId) => {
        if (clinicId === undefined || clinicId === null || clinicId === '') return null;

        const id = typeof clinicId === 'string' ? parseInt(clinicId, 10) : clinicId;

        if (isNaN(id) || id <= 0) {
            throw new Error('clinic_id must be a positive number');
        }

        return id;
    },

    // Validate and normalize doctor ID
    normalizeDoctorId: (doctorId) => {
        if (doctorId === undefined || doctorId === null || doctorId === '') return null;

        const id = typeof doctorId === 'string' ? parseInt(doctorId, 10) : doctorId;

        if (isNaN(id) || id <= 0) {
            throw new Error('doctor_id must be a positive number');
        }

        return id;
    },

    // Validate ID exists and is accessible to user
    validateIdAccess: (id, userAccessibleIds, idType = 'ID') => {
        if (!id) return { valid: true }; // Optional IDs are valid when null/undefined

        if (!Array.isArray(userAccessibleIds) || userAccessibleIds.length === 0) {
            return { valid: false, error: `No accessible ${idType}s found for user` };
        }

        const normalizedId = typeof id === 'string' ? parseInt(id, 10) : id;

        if (!userAccessibleIds.includes(normalizedId)) {
            return { valid: false, error: `Access denied to ${idType} ${normalizedId}` };
        }

        return { valid: true };
    }
};

// Role-based parameter filtering
export const roleBasedFiltering = {
    // Define role hierarchy and permissions
    ROLES: {
        ADMIN: 'admin',
        CLINIC_OWNER: 'clinic_owner',
        DOCTOR: 'doctor',
        FRONTDESK: 'frontdesk'
    },

    // Filter parameters based on user role and permissions
    filterParamsByRole: (params, userRole, userContext = {}) => {
        const filtered = { ...params };

        switch (userRole) {
            case roleBasedFiltering.ROLES.ADMIN:
                // Admin can access all parameters as provided
                break;

            case roleBasedFiltering.ROLES.CLINIC_OWNER:
                // Clinic owner can only access their clinic's data
                if (userContext.clinicId) {
                    filtered.clinic_id = userContext.clinicId;
                }
                // Can filter by doctors within their clinic
                if (filtered.doctor_id && userContext.accessibleDoctorIds) {
                    const doctorAccess = idUtils.validateIdAccess(
                        filtered.doctor_id,
                        userContext.accessibleDoctorIds,
                        'doctor'
                    );
                    if (!doctorAccess.valid) {
                        throw new Error(doctorAccess.error);
                    }
                }
                break;

            case roleBasedFiltering.ROLES.DOCTOR:
                // Doctor can only access their own data
                filtered.doctor_id = userContext.doctorId;
                filtered.clinic_id = userContext.clinicId;
                break;

            case roleBasedFiltering.ROLES.FRONTDESK:
                // Frontdesk can access clinic-level data but not specific doctors
                if (userContext.clinicId) {
                    filtered.clinic_id = userContext.clinicId;
                }
                // Remove doctor_id for frontdesk users
                delete filtered.doctor_id;
                break;

            default:
                throw new Error(`Unknown user role: ${userRole}`);
        }

        return filtered;
    },

    // Get accessible filter options based on role
    getAccessibleOptions: (userRole, userContext = {}) => {
        const options = {
            canSelectClinic: false,
            canSelectDoctor: false,
            availableClinics: [],
            availableDoctors: []
        };

        switch (userRole) {
            case roleBasedFiltering.ROLES.ADMIN:
                options.canSelectClinic = true;
                options.canSelectDoctor = true;
                options.availableClinics = userContext.allClinics || [];
                options.availableDoctors = userContext.allDoctors || [];
                break;

            case roleBasedFiltering.ROLES.CLINIC_OWNER:
                options.canSelectDoctor = true;
                options.availableClinics = userContext.ownedClinics || [];
                options.availableDoctors = userContext.accessibleDoctors || [];
                break;

            case roleBasedFiltering.ROLES.DOCTOR:
                // Doctors cannot change clinic/doctor selection
                break;

            case roleBasedFiltering.ROLES.FRONTDESK:
                // Frontdesk cannot change selections
                break;
        }

        return options;
    }
};

// Main parameter formatting function
export const formatReportParams = (rawParams = {}, userRole, userContext = {}) => {
    try {
        // Start with a clean params object
        const params = {};

        // Handle date parameters
        if (rawParams.startDate || rawParams.endDate) {
            const startDate = dateUtils.formatDate(rawParams.startDate);
            const endDate = dateUtils.formatDate(rawParams.endDate);

            const dateValidation = dateUtils.validateDateRange(startDate, endDate);
            if (!dateValidation.valid) {
                throw new Error(dateValidation.error);
            }

            params.startDate = startDate;
            params.endDate = endDate;
        }

        // Handle ID parameters
        if (rawParams.clinic_id !== undefined) {
            const clinicId = idUtils.normalizeClinicId(rawParams.clinic_id);
            if (clinicId !== null) params.clinic_id = clinicId;
        }

        if (rawParams.doctor_id !== undefined) {
            const doctorId = idUtils.normalizeDoctorId(rawParams.doctor_id);
            if (doctorId !== null) params.doctor_id = doctorId;
        }

        // Apply role-based filtering
        const filteredParams = roleBasedFiltering.filterParamsByRole(params, userRole, userContext);

        // Add JWT token if provided
        if (rawParams.jwt) {
            filteredParams.jwt = rawParams.jwt;
        }

        return filteredParams;

    } catch (error) {
        throw new Error(`Parameter formatting failed: ${error.message}`);
    }
};

// Validation function for required parameters
export const validateRequiredParams = (params, requiredFields = []) => {
    const missing = [];

    requiredFields.forEach(field => {
        if (field.includes('Date')) {
            if (!params[field] || !dateUtils.isValidDate(params[field])) {
                missing.push(`${field} (YYYY-MM-DD format)`);
            }
        } else if (params[field] === undefined || params[field] === null) {
            missing.push(field);
        }
    });

    if (missing.length > 0) {
        throw new Error(`Missing required parameters: ${missing.join(', ')}`);
    }

    return true;
};

// Export default object with all utilities
const reportParams = {
    dateUtils,
    idUtils,
    roleBasedFiltering,
    formatReportParams,
    validateRequiredParams
};

export default reportParams;